import { ChangeEvent, FC, FocusEvent, useEffect, useRef, useState } from 'react';
import { InputContainer, Li, Options, Select, SelectedOption, Ul } from './Dropdown.styles';
import { ExpandMore } from '@mui/icons-material';
import { handleKeyDown } from '../../utils';
import { Input } from '../input';
import { Grid } from '@mui/material';

export type Option = {
	value: string | number;
	label: string;
};

interface DropdownProps {
	options: Option[];
	selected?: Option;
	placeholder?: string;
	disabled?: boolean;
	searchable?: boolean;
	onSelect?: (option: Option) => void;
	icon?:boolean;
}

const Dropdown: FC<DropdownProps> = ({
	options,
	selected,
	placeholder = 'Selecione...',
	disabled,
	searchable,
	onSelect,
	icon
}) => {
	const inputRef = useRef<HTMLDivElement>(null);
	const [expanded, setExpanded] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredOptions, setFilteredOptions] = useState(options);

	const handleExpand = () => {
		if (disabled) return;
		setExpanded(!expanded);
	};

	const handleCollapse = (e: FocusEvent) => {
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setExpanded(false);
		}
	};

	const handleSelect = (option: Option) => {
		onSelect?.(option);
		setExpanded(false);
	};

	const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	useEffect(() => {
		if (searchable) {
			setFilteredOptions(
				options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
			);

			return;
		}

		setFilteredOptions(options);
	}, [searchTerm, options, searchable]);

	useEffect(() => {
		if (expanded && searchable) {
			inputRef.current?.getElementsByTagName('input')[0].focus();
		}
	}, [expanded, searchable]);

	return (
		<Select
			role='listbox'
			onClick={handleExpand}
			onKeyDown={handleKeyDown(handleExpand)}
			onBlur={handleCollapse}
			tabIndex={0}
			$disabled={disabled}
		>
			<SelectedOption $expanded={expanded} $placeholder={!selected}>
				<span>{selected?.label || placeholder}</span>
				<ExpandMore />
				<Options $expanded={expanded}>
					<Ul role='list' $expanded={expanded}>
						{searchable && (
							<InputContainer ref={inputRef} onClick={(e) => e.stopPropagation()}>
								<Input variant='search' value={searchTerm} onChange={handleSearch} />
							</InputContainer>
						)}
						{filteredOptions.map((option) => (
							<Li
								role='listitem'
								key={option.value}
								$selected={selected?.value === option.value}
								onClick={() => handleSelect(option)}
								onKeyDown={handleKeyDown(() => handleSelect(option))}
								tabIndex={0}
							>
								{icon ? (
									<Grid
										style={{
											width:"80px",
											backgroundColor:
											option.value === 'ativos'
												? 'rgb(6, 194, 112)' // Cor para 'active'
												: option.value === 'inativos'
												? 'rgb(255, 99, 71)' // Cor para 'inactive'
												: 'rgb(100, 149, 237)',
											padding: "4px 8px",
											borderRadius: "8px",
											textAlign:"center",
											color:"#fff"
										}}
									>
										{option.label}
									</Grid>
								) : (
									option.label
								)}
							</Li>
						))}
					</Ul>
				</Options>
			</SelectedOption>
		</Select>
	);
};

export default Dropdown;
